export const VIDEO_CODEC_CANDIDATES = [
  { codec: "avc1.42001f", label: "H.264/AVC", avc: { format: "annexb" } },
  { codec: "avc1.420028", label: "H.264/AVC 1080p", avc: { format: "annexb" } },
  { codec: "vp09.00.30.08.00", label: "VP9" },
  { codec: "vp8", label: "VP8" },
  { codec: "av01.0.05M.08", label: "AV1" },
  { codec: "hvc1.1.6.L120.B0", label: "HEVC/H.265" }
];

export const LOW_VIDEO_MODES = {
  auto: { width: 480, height: 270, bitrate: 200_000, framerate: 12, preference: "auto" },
  avc360: { width: 640, height: 360, bitrate: 300_000, framerate: 15, preference: "avc1.42001f" },
  avc270: { width: 480, height: 270, bitrate: 200_000, framerate: 12, preference: "avc1.42001f" },
  hevc270: { width: 480, height: 270, bitrate: 150_000, framerate: 12, preference: "hvc1.1.6.L120.B0" },
  vp9270: { width: 480, height: 270, bitrate: 170_000, framerate: 12, preference: "vp09.00.30.08.00" },
  vp8180: { width: 320, height: 180, bitrate: 150_000, framerate: 10, preference: "vp8" },
  av1180: { width: 320, height: 180, bitrate: 80_000, framerate: 8, preference: "av01.0.05M.08" }
};

export const HIGH_VIDEO_MODES = {
  auto: { width: 1280, height: 720, bitrate: 1_600_000, framerate: 30, preference: "auto" },
  avc1080: { width: 1920, height: 1080, bitrate: 2_800_000, framerate: 30, preference: "avc1.420028", hardwareOnly: true },
  avc720: { width: 1280, height: 720, bitrate: 1_600_000, framerate: 30, preference: "avc1.42001f" },
  vp8720: { width: 1280, height: 720, bitrate: 1_600_000, framerate: 30, preference: "vp8" },
  vp9720: { width: 1280, height: 720, bitrate: 1_100_000, framerate: 24, preference: "vp09.00.30.08.00" },
  hevc720: { width: 1280, height: 720, bitrate: 1_000_000, framerate: 30, preference: "hvc1.1.6.L120.B0" },
  av1540: { width: 960, height: 540, bitrate: 650_000, framerate: 20, preference: "av01.0.05M.08" }
};

const IS_FIREFOX = /Firefox\//.test(navigator.userAgent);

export function videoEncoderConfig(codec, track, hardwareAcceleration = "prefer-hardware") {
  return {
    codec: codec.codec, width: track.width, height: track.height, bitrate: track.bitrate, framerate: track.framerate,
    latencyMode: "realtime", hardwareAcceleration, ...(codec.avc ? { avc: codec.avc } : {})
  };
}

export function autoCodecCandidates(quality) {
  const priority = IS_FIREFOX
    ? quality === "high"
      ? ["vp8", "vp09.00.30.08.00", "avc1.42001f", "av01.0.05M.08", "hvc1.1.6.L120.B0"]
      : ["vp8", "avc1.42001f", "vp09.00.30.08.00", "av01.0.05M.08", "hvc1.1.6.L120.B0"]
    : quality === "high"
      ? ["avc1.42001f", "vp8", "vp09.00.30.08.00", "hvc1.1.6.L120.B0", "av01.0.05M.08"]
      : ["avc1.42001f", "hvc1.1.6.L120.B0", "vp09.00.30.08.00", "vp8", "av01.0.05M.08"];
  return priority.map(codec => VIDEO_CODEC_CANDIDATES.find(candidate => candidate.codec === codec)).filter(Boolean);
}

export async function selectVideoCodec(track, preference, hardwareOnly = false) {
  const candidates = preference === "auto"
    ? autoCodecCandidates(track.quality)
    : VIDEO_CODEC_CANDIDATES.filter(candidate => candidate.codec === preference);
  for (const acceleration of hardwareOnly ? ["prefer-hardware"] : ["prefer-hardware", "prefer-software"]) {
    for (const codec of candidates) {
      const support = await VideoEncoder.isConfigSupported(videoEncoderConfig(codec, track, acceleration)).catch(() => ({ supported: false }));
      if (support.supported) return { codec, acceleration };
    }
  }
  return undefined;
}

export async function isVideoModeSupported(mode, quality) {
  if (!globalThis.VideoEncoder) return false;
  return Boolean(await selectVideoCodec({ ...mode, quality }, mode.preference, mode.hardwareOnly));
}

export async function refreshModeSelect(select, modes, quality) {
  for (const option of select.options) {
    const mode = modes[option.value];
    if (!mode) continue;
    option.dataset.label ??= option.textContent;
    const supported = await isVideoModeSupported(mode, quality);
    option.disabled = !supported;
    option.textContent = supported ? option.dataset.label : `${option.dataset.label} · unavailable`;
    if (!supported && select.value === option.value) select.value = "auto";
  }
}
